/**
 * PurchaseOrderDetailPage (S3.2) — PO detail rebuilt on the house trio
 * pattern (SoHeader/VendorDetailPage precedent): PoHeader card (status Tag,
 * source-SO ref, >NT$200k approval banner w/ ROLE-GATED release/reject) +
 * 248px Sider section menu + section content. Replaces the codex
 * `purchaseOrders/PurchaseOrderDetailPage.tsx`.
 *
 * - `?section=` deep-link: the active section is ROUTE-DERIVED (query
 *   param), never local-only state (house selection tenet).
 * - Sections: 訂單明細 lines (linked SO refs via first-class Allocations +
 *   檢查第二次 aids) · 供應商確認 checkpoints (A1/A2/A3 timeline +
 *   VendorConfirmDrawer) · 稽核 audit.
 * - Release/reject go through ReasonRequiredModal (actor = signed-in user,
 *   mandatory logged reason — the S3.1 decision contract).
 */

import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { App, Card, Grid, Layout, Menu, Spin, theme } from 'antd';
import { AuditOutlined, CheckSquareOutlined, ProfileOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ListErrorState } from '@/components/common/TableContainer';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import {
  useOccLinesForPo,
  usePurchaseOrder,
  usePurchaseOrderMutations,
} from '@/hooks/usePurchaseOrders';
import { extractApiError } from '@/hooks/useSalesOrders';
import { usePoStore } from '@/stores/poStore';
import { getUserProfile } from '@/services/api/axios.config';
import type { User } from '@/models/user';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import VendorConfirmDrawer from '../VendorConfirmDrawer';
import PoHeader from './PoHeader';
import LinesSection from './sections/LinesSection';
import CheckpointsSection from './sections/CheckpointsSection';
import AuditSection from './sections/AuditSection';
import { canDecidePoApproval } from './poPermissions';
import PageContainer from '@/components/common/PageContainer';

const { Sider, Content } = Layout;

const PO_DETAIL_SECTION_KEYS = ['lines', 'checkpoints', 'audit'] as const;
type PoDetailSectionKey = (typeof PO_DETAIL_SECTION_KEYS)[number];

const SECTION_ICONS: Record<PoDetailSectionKey, React.ReactNode> = {
  lines: <ProfileOutlined />,
  checkpoints: <CheckSquareOutlined />,
  audit: <AuditOutlined />,
};

const PurchaseOrderDetailPage = () => {
  const { t } = useTranslation(['procurement', 'common']);
  const { poId } = useParams<{ poId: string }>();
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const elevation = useElevation();
  const screens = Grid.useBreakpoint();

  // ?section= deep-link — route-derived selection (falls back to 'lines')
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');
  const activeSection: PoDetailSectionKey = PO_DETAIL_SECTION_KEYS.includes(
    sectionParam as PoDetailSectionKey
  )
    ? (sectionParam as PoDetailSectionKey)
    : 'lines';

  const setActiveSection = (section: PoDetailSectionKey) => {
    setSearchParams(section === 'lines' ? {} : { section });
  };

  const { purchaseOrder: po, isLoading, error, refresh } = usePurchaseOrder(poId);
  const {
    occLines,
    refresh: refreshOcc,
  } = useOccLinesForPo(poId);
  const { sendPurchaseOrder, decidePurchaseOrder } = usePurchaseOrderMutations();

  // Select the RAW array (stable snapshot) and derive with useMemo — an
  // inline .filter selector returns a new array every call and trips
  // React's getSnapshot loop guard.
  const allCheckpointRecords = usePoStore((state) => state.checkpointRecords);
  const checkpointRecords = useMemo(
    () => allCheckpointRecords.filter((record) => record.poId === poId),
    [allCheckpointRecords, poId]
  );

  const canDecide = canDecidePoApproval();

  const [sending, setSending] = useState(false);
  const [decision, setDecision] = useState<'approve' | 'reject' | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // A checkpoint can be captured once the vendor has the order.
  const showConfirmCheckpoint =
    !!po && (po.status === 'sent' || po.status === 'vendor_confirmed' || po.status === 'shipped');

  const handleSend = async () => {
    if (!po) return;
    setSending(true);
    try {
      await sendPurchaseOrder(po.id);
      message.success(t('procurement:messages.sent'));
      refresh();
    } catch (err) {
      const { message: detail } = extractApiError(err);
      message.error(detail || t('common:messages.updateFailed'));
    } finally {
      setSending(false);
    }
  };

  const handleDecision = async (reason: string) => {
    if (!po || !decision) return;
    const actor = (getUserProfile() as User | null)?.name ?? 'manager';
    try {
      await decidePurchaseOrder(po.id, decision, actor, reason);
      message.success(
        decision === 'approve'
          ? t('procurement:messages.released')
          : t('procurement:messages.rejected')
      );
      setDecision(null);
      refresh();
    } catch (err) {
      const { message: detail } = extractApiError(err);
      message.error(detail || t('common:messages.updateFailed'));
      return false;
    }
  };

  const handleCheckpointSaved = () => {
    refresh();
    refreshOcc();
  };

  if (error) {
    return (
      <PageContainer>
        <Card style={{ borderRadius: radiusTokens.panel }}>
          <ListErrorState
            title={t('procurement:detail.loadFailed')}
            retryLabel={t('common:errors.boundary.retry')}
            onRetry={refresh}
          />
        </Card>
      </PageContainer>
    );
  }

  const menuItems = PO_DETAIL_SECTION_KEYS.map((key) => ({
    key,
    icon: SECTION_ICONS[key],
    label: t(`procurement:detail.sections.${key}`),
  }));

  const renderSection = () => {
    if (!po) return null;
    switch (activeSection) {
      case 'lines':
        return <LinesSection po={po} />;
      case 'checkpoints':
        return (
          <CheckpointsSection
            po={po}
            occLines={occLines}
            records={checkpointRecords}
            showConfirmCheckpoint={showConfirmCheckpoint}
            onOpenConfirm={() => setConfirmOpen(true)}
          />
        );
      case 'audit':
        return <AuditSection po={po} records={checkpointRecords} />;
    }
  };

  return (
    <PageContainer>
      <Spin spinning={isLoading}>
        {po && (
          <>
            <PoHeader
              po={po}
              canDecide={canDecide}
              sending={sending}
              showConfirmCheckpoint={showConfirmCheckpoint}
              onSend={() => void handleSend()}
              onRelease={() => setDecision('approve')}
              onReject={() => setDecision('reject')}
              onOpenConfirm={() => {
                setActiveSection('checkpoints');
                setConfirmOpen(true);
              }}
            />

            <Card
              style={{
                ...elevation.card,
                overflow: 'hidden',
              }}
              styles={{ body: { padding: 0 } }}
            >
              <Layout style={{ backgroundColor: 'transparent' }}>
                {screens.lg && (
                  <Sider
                    width={248}
                    theme="light"
                    style={{
                      borderRight: `1px solid ${token.colorBorderSecondary}`,
                      padding: 0,
                      backgroundColor: 'transparent',
                    }}
                  >
                    <style>
                      {`
                        .po-detail-menu {
                          padding: 0 12px !important;
                        }
                        .po-detail-menu .ant-menu-item {
                          height: ${controlHeightTokens.controlHeightXL}px !important;
                          line-height: ${controlHeightTokens.controlHeightXL}px !important;
                          margin: 4px 0 !important;
                          display: flex !important;
                          align-items: center !important;
                          border-radius: ${radiusTokens.card}px !important;
                          width: 100% !important;
                        }
                      `}
                    </style>
                    <Menu
                      mode="inline"
                      selectedKeys={[activeSection]}
                      items={menuItems}
                      onClick={({ key }) => setActiveSection(key as PoDetailSectionKey)}
                      className="po-detail-menu"
                      style={{ borderRight: 'none', backgroundColor: 'transparent' }}
                    />
                  </Sider>
                )}
                <Content style={{ padding: '24px', minHeight: '400px', minWidth: 0 }}>
                  {!screens.lg && (
                    <Menu
                      mode="horizontal"
                      selectedKeys={[activeSection]}
                      items={menuItems}
                      onClick={({ key }) => setActiveSection(key as PoDetailSectionKey)}
                      style={{ marginBottom: token.marginLG }}
                    />
                  )}
                  {renderSection()}
                </Content>
              </Layout>
            </Card>

            {/* >NT$200k release/reject — mandatory logged reason (S3.1) */}
            <ReasonRequiredModal
              open={decision !== null}
              title={
                decision === 'reject'
                  ? t('procurement:detail.reject.title')
                  : t('procurement:detail.release.title')
              }
              description={
                decision === 'reject'
                  ? t('procurement:detail.reject.description')
                  : t('procurement:detail.release.description')
              }
              submitText={
                decision === 'reject'
                  ? t('procurement:detail.reject.submit')
                  : t('procurement:detail.release.submit')
              }
              cancelText={t('common:actions.cancel')}
              reasonLabel={t('procurement:detail.release.reasonLabel')}
              requiredMessage={t('procurement:detail.release.reasonRequired')}
              reasonPlaceholder={
                decision === 'reject'
                  ? t('procurement:detail.reject.reasonPlaceholder')
                  : t('procurement:detail.release.reasonPlaceholder')
              }
              onSubmit={handleDecision}
              onCancel={() => setDecision(null)}
            />

            <VendorConfirmDrawer
              open={confirmOpen}
              po={po}
              occLines={occLines}
              records={checkpointRecords}
              onClose={() => setConfirmOpen(false)}
              onSaved={handleCheckpointSaved}
            />
          </>
        )}
      </Spin>
    </PageContainer>
  );
};

export default PurchaseOrderDetailPage;
